import autoBind from 'auto-bind';
import * as midiManager from 'midi-file';
import { MidiData, MidiSetTempoEvent } from 'midi-file';
import { Voice, VoiceValues } from '../../../common/src/model.ts';
import { NetworkError } from '../util/network.ts';
import { DualAudioElementInterface } from './dual_audio_element.tsx';
import { FirstNotePlayer } from './first_note_player.ts';
import { generateWavBlobForMIDI, loadInstrument, MP3GeneratorResult, MP3Inputs } from './instrument.ts';
import { MP3AudioPlayer } from './mp3_audio_player.ts';

const microsecondsPerMinute = 60000000;

export class MidiFilePlayer extends MP3AudioPlayer {
  isMidiFileLoaded: boolean = false;
  isInstrumentLoaded?: boolean;
  midiFilePath: string;
  firstNotePlayer: FirstNotePlayer;
  setSongReady: () => void;
  onNetworkError: (networkError: NetworkError) => void;
  onVoiceChange?: (voice: Voice[]) => void;
  modulationHalfSteps: () => number;
  private _midiData: MidiData | undefined;
  getCurrentTempo: () => number | undefined;
  currentMP3Inputs: MP3Inputs | undefined;

  constructor({
    dualAudioElement,
    midiFilePath,
    setSongReady,
    onNetworkError,
    onVoiceChange,
    endOfFile,
    modulationHalfSteps,
    onDurationChange,
    getCurrentTempo,
    updateLocationSlider,
    getVoiceValues,
  }: {
    dualAudioElement: DualAudioElementInterface;
    midiFilePath: string;
    setSongReady: () => void;
    onNetworkError: (networkError: NetworkError) => void;
    onVoiceChange?: (voiceNames: Voice[]) => void;
    endOfFile: () => void;
    modulationHalfSteps: () => number;
    onDurationChange?: (duration: number | undefined) => void;
    getCurrentTempo: () => number | undefined;
    updateLocationSlider?: () => void;
    getVoiceValues: () => VoiceValues;
  }) {
    super({ dualAudioElement, endOfFile, onDurationChange, updateLocationSlider, getVoiceValues });
    autoBind(this);

    this.midiFilePath = midiFilePath;
    this.setSongReady = setSongReady;
    this.onNetworkError = onNetworkError;
    this.onVoiceChange = onVoiceChange;
    this.modulationHalfSteps = modulationHalfSteps;
    this.getCurrentTempo = getCurrentTempo;

    this.firstNotePlayer = new FirstNotePlayer({ modulationHalfSteps, getSongMidiData: () => this._midiData });

    void loadInstrument(async () => {
      this.isInstrumentLoaded = true;
      await this._loadMidiFile();
      if (this.isMidiFileLoaded && this.duration !== undefined) {
        this.setSongReady();
      }
    });
  }

  destroy() {
    // stop music when back button is pressed
    super.destroy();
    this.firstNotePlayer.destroy();
  }

  protected onCanPlayThrough() {
    super.onCanPlayThrough();
    if (!this.isMidiFileLoaded) {
      // Apple WebView still doesn't know the song duration during "canplaythrough"... :)
      // This causes UI update problems because the duration is expected to be known.
      // So, we wait for the duration change to really know that the song is ready.
      // Apple WebView also does not fire "durationchange"... :( ...how bad can they be at software?
      // So, we keep trying until we get a duration...
      if (this.duration === undefined) {
        setTimeout(() => this.onCanPlayThrough(), 50);
        return;
      }
      this.onVoiceChange?.(this.voices);
      this.isMidiFileLoaded = true;
      if (this.isInstrumentLoaded) {
        this.setSongReady();
      }
    }
  }

  play() {
    this.firstNotePlayer.pause();
    super.play();
  }

  pause() {
    super.pause();
    this.firstNotePlayer.pause(); // in case first note is playing when player closes
  }

  get fileTempo(): number | undefined {
    if (!this._midiData) {
      return;
    }
    const tempoEvent = this._midiData.tracks[0].find((event) => event.type === 'setTempo') as
      MidiSetTempoEvent | undefined;
    if (tempoEvent) {
      return tempoEvent.microsecondsPerBeat ? Math.round(microsecondsPerMinute / tempoEvent.microsecondsPerBeat) : 100;
    }
  }

  private async _loadMidiFile() {
    const loadArrayBuffer = async (buffer: ArrayBuffer) => {
      try {
        const midiArray = new Uint8Array(buffer);
        this._midiData = midiManager.parseMidi(midiArray);
        this.initialTime = '0:00';
        await this.refreshMP3();
      } catch (error: any) {
        console.error(`Error parsing MIDI file ${this.midiFilePath}: ${error.message}`);
        this.onNetworkError(NetworkError.MidiFileParseFailed);
      }
    };

    const response = await fetch(this.midiFilePath);
    if (!response.ok) {
      // an expected transient, not an error - see onNetworkError in sequencer.tsx (issue #3265)
      console.log(`Error fetching MIDI file ${this.midiFilePath}: ${response.statusText}`);
      this.onNetworkError(NetworkError.MidiDownloadFailed);
      return;
    }
    const buffer = await response.arrayBuffer();
    await loadArrayBuffer(buffer);
  }

  get voices(): Voice[] {
    const tracksWithNoteEvents =
      this._midiData?.tracks?.filter((events) => events.some(({ type }) => type === 'noteOn' || type === 'noteOff')) ??
      [];
    const nameEvents =
      tracksWithNoteEvents.flatMap((events) => events.filter((event) => event.type === 'trackName')) ?? [];
    const trackNames = nameEvents.map(({ text }) => text?.toLowerCase()).filter((text) => text);
    const uniqueTrackNames = [...new Set(trackNames)];
    return uniqueTrackNames as Voice[];
  }

  playFirstNote(voice: Voice, startTick?: number) {
    this.firstNotePlayer.play(voice, startTick);
  }

  get ticksPerBeat(): number | undefined {
    return this._midiData?.header.ticksPerBeat;
  }

  protected async refreshMP3() {
    const tempo = this.getCurrentTempo();
    if (!this._midiData || !tempo) {
      return;
    }
    // have any inputs changed since last MP3 generation?
    const newInputs = { tempo, modulationHalfSteps: this.modulationHalfSteps(), voiceValues: this.getVoiceValues() };
    const previousInputs = this.currentMP3Inputs;
    if (JSON.stringify(newInputs) === JSON.stringify(previousInputs)) {
      return;
    }
    this.currentMP3Inputs = newInputs; // optimistically guard against reentrancy
    const currentIteration = this.abortPreviousMP3Refresh();
    const shouldAbort = () => this.shouldAbortMP3Refresh(currentIteration);
    const { audioBlob, result } = await generateWavBlobForMIDI(this._midiData, newInputs, shouldAbort);
    if (result === MP3GeneratorResult.Abort) {
      console.log(`aborted _refreshMP3`);
      return;
    }
    if (result === MP3GeneratorResult.Failure) {
      console.error(`_refreshMP3: failed to generate MP3`);
      this.onNetworkError(NetworkError.MP3GenerationFailed);
      this.currentMP3Inputs = previousInputs;
      return;
    }
    if (!audioBlob) {
      console.error(`_refreshMP3: audioBlob is undefined`);
      this.currentMP3Inputs = previousInputs;
      return;
    }
    await this.audioElement.setSrc(audioBlob);
  }
}
